import { MoreHoriz } from '@mui/icons-material';
import type { MenuListProps } from '@mui/material';
import type * as React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { focusableDisabledItems } from '~/components/menu-item';
import { useUniqueId } from './unique-id';

export interface MoreMenuProps<T> {
  anchorEl: HTMLElement | null;
  context: T | undefined;
  id: string;
  keepMounted: boolean;
  MenuListProps: Partial<MenuListProps>;
  onClose: () => void;
  open: boolean;
}

export interface MoreActionProps {
  closeMenu: () => void;
}

export const useMoreActions = <T = never>() => {
  const { t } = useTranslation();
  const menuId = useUniqueId('more-actions-menu');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [context, setContext] = useState<T>();

  const onClose = useCallback(() => setAnchorEl(null), []);

  const open = anchorEl !== null;

  const moreMenuProps: MoreMenuProps<T> = {
    anchorEl,
    context,
    id: menuId,
    keepMounted: true,
    MenuListProps: focusableDisabledItems,
    onClose,
    open,
  };

  const moreTableActionProps = useCallback(
    (value: T) => ({
      'aria-controls': menuId,
      Icon: MoreHoriz,
      title: t('common.buttons.more_actions'),
      onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setContext(value);
        setAnchorEl(event.currentTarget);
      },
    }),
    [menuId, setAnchorEl, setContext, t],
  );

  const moreActionProps: MoreActionProps = { closeMenu: onClose };

  return [moreMenuProps, moreActionProps, moreTableActionProps] as const;
};
